<template>
  <div>
    <div class="mb-2 flex items-center justify-between px-1">
      <span class="text-[11px] font-bold text-deen-ink/70">{{ label }}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-deen-ink/40" aria-hidden="true"><path d="M19 14c1.490-1.460 3-3.210 3-5.500A5.500 5.500 0 0 0 16.500 3c-1.760 0-3 .5-4.500 2-1.500-1.500-2.740-2-4.500-2A5.500 5.500 0 0 0 2 8.500c0 2.300 1.500 4.050 3 5.500l7 7Z"/></svg>
    </div>

    <div v-if="post.kind === 'ayah'" :class="verses.length > 1 ? '-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1' : ''">
      <div
        v-for="verse in verses"
        :key="verse.id ?? verse.reference"
        class="rounded-[1.75rem] bg-[#1c4848] px-5 py-6 text-center"
        :class="verses.length > 1 ? 'w-[88%] shrink-0 snap-start' : ''"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ecd79f" stroke-width="1.500" stroke-linejoin="round" class="mx-auto mb-3" aria-hidden="true"><path d="M12 3l1.900 5.100L19 10l-5.100 1.900L12 17l-1.900-5.100L5 10l5.100-1.900z"/></svg>
        <p dir="rtl" lang="ar" class="font-quran text-xl leading-[2] text-white mb-3">{{ verse.arabic }}</p>
        <p class="font-display italic text-sm leading-relaxed text-white/75 mb-4">{{ verse.text }}</p>
        <div class="h-px bg-white/10 mb-3"></div>
        <p v-if="verse.reference" class="text-[10px] font-semibold text-deen-gold">{{ verse.reference }}</p>
        <p class="text-[9px] font-bold uppercase tracking-[0.2em] text-deen-gold/70">{{ strings.quran }}</p>
      </div>
    </div>

    <div v-else-if="post.kind === 'hadith'" class="relative rounded-[1.75rem] bg-gradient-to-br from-[#fafbfc] to-[#f4f6f6] px-5 pb-5 pt-7 text-center ring-1 ring-deen-ink/5">
      <span class="absolute left-3 top-1 font-display text-5xl leading-none text-deen-gold/40" aria-hidden="true">“</span>
      <p class="relative italic text-[15px] leading-relaxed text-deen-ink">{{ post.text }}</p>
      <p v-if="post.source" class="mt-3 inline-block rounded-full bg-white px-3 py-1 text-[9px] font-bold text-[#125145] ring-1 ring-deen-ink/5">{{ post.source }}</p>
    </div>

    <div v-else-if="post.kind === 'page'" class="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1">
      <div v-for="page in pages" :key="page.id ?? page.title" class="w-[230px] shrink-0 snap-start rounded-3xl bg-white p-5 ring-1 ring-deen-ink/5">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ecd79f" stroke-width="2" class="mb-3" aria-hidden="true"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>
        <p class="text-xs font-semibold leading-snug text-deen-ink line-clamp-3">{{ page.text || page.title }}</p>
        <p class="mt-3 text-[9px] font-bold uppercase text-deen-ink/50">{{ shortTitle(page.title) }}</p>
      </div>
    </div>

    <div v-else class="rounded-[1.75rem] bg-white p-4 ring-1 ring-deen-ink/5">
      <p v-if="post.kind === 'book' && !post.kids" class="mb-3 inline-flex items-center gap-1.5 rounded-full bg-deen-gold/10 px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-[#125145]">
        <span class="h-1.5 w-1.5 rounded-full bg-deen-gold" aria-hidden="true"></span>{{ strings.newRelease }}
      </p>
      <div class="flex gap-3">
        <div class="flex h-24 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#1c4848]">
          <img v-if="post.image" :src="post.image" alt="" loading="lazy" class="h-full w-full object-cover">
          <span v-else class="font-display text-2xl text-deen-gold" aria-hidden="true">{{ (post.title ?? '?').slice(0, 1) }}</span>
        </div>
        <div class="min-w-0">
          <p class="text-sm font-bold leading-snug text-deen-ink line-clamp-3">{{ post.title }}</p>
          <p v-if="post.text" class="mt-1 text-[11px] leading-snug text-deen-ink/60 line-clamp-4">{{ post.text }}</p>
          <p v-if="post.kind === 'podcast' && post.source" class="mt-1 text-[11px] tabular-nums text-deen-ink/60">{{ post.source }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

/** A post drawn the way the iOS app draws it, with the app's own labels. */
const props = defineProps({
  post: { type: Object, required: true },
  languageId: { type: Number, default: 1 },
});

const STRINGS = {
  en: { ayah: 'Verse of the Day', hadith: 'Hadith of the Day', book: 'Recommended Book', podcast: 'Lecture Series', page: 'Wisdom', quran: 'Quran', newRelease: 'New Release' },
  bs: { ayah: 'Dnevni ajet', hadith: 'Hadis dana', book: 'Preporučena knjiga', podcast: 'Serija predavanja', page: 'Mudrost', quran: "Kur'an", newRelease: 'Novo izdanje' },
};

const strings = computed(() => (props.languageId === 2 ? STRINGS.bs : STRINGS.en));
const label = computed(() => (props.post.kids ? 'Kids' : strings.value[props.post.kind] ?? ''));
const pages = computed(() => props.post.pages ?? [props.post]);
const verses = computed(() => (props.post.verses?.length ? props.post.verses : [props.post]));

function shortTitle(title = '') {
  return title.length > 30 ? `${title.slice(0, 30)}…` : title;
}
</script>
